import { useEffect, useMemo, useState } from 'react'
import { usePermission } from '@/features/auth/store'
import { useAllPermissions, useRolePermissions, useSetRolePermissions } from '../hooks'
import { PermissionGroup } from './PermissionGroup'
import { groupPermissionsByDomain } from './groupPermissionsByDomain'
import { domainLabelPl } from './permissionDomainLabels'
import { formatError } from './formatError'

/**
 * Nadawanie/odbieranie uprawnień (PERM) roli. Checkboxy pogrupowane po domenie.
 * Gated PERM-em `access.permissions.edit` (backend wymusza to samo via @PreAuthorize).
 */
export function RolePermissionsPanel({ roleCode }: { roleCode: string }) {
  const canEdit = usePermission('access.permissions.edit')
  const allQ = useAllPermissions()
  const roleQ = useRolePermissions(roleCode)
  const save = useSetRolePermissions(roleCode)

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (roleQ.data) setSelected(new Set(roleQ.data))
  }, [roleQ.data])

  const groups = useMemo(() => groupPermissionsByDomain(allQ.data ?? []), [allQ.data])

  if (!canEdit) return <p className="text-sm text-slate-500">Brak dostępu.</p>
  if (allQ.isLoading || roleQ.isLoading) return <p className="text-xs text-slate-500">Ładowanie…</p>
  if (!allQ.data || !roleQ.data) return null

  const toggle = (code: string) => {
    setError(null)
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }

  const onSave = () => {
    setError(null)
    save.mutate([...selected], { onError: e => setError(formatError(e)) })
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4">
        {groups.map(([domain, perms]) => (
          <PermissionGroup
            key={domain}
            label={domainLabelPl(domain)}
            permissions={perms}
            selected={selected}
            onToggle={toggle}
            disabled={save.isPending}
          />
        ))}
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onSave}
          disabled={save.isPending}
          className="rounded bg-sky-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-60"
        >
          {save.isPending ? 'Zapisywanie…' : 'Zapisz'}
        </button>
        {save.isSuccess && !save.isPending && !error && (
          <span className="text-xs text-emerald-600">Zapisano.</span>
        )}
        {error && <span className="text-xs text-red-600">{error}</span>}
      </div>
    </div>
  )
}
